/**
 * Keyed reconciliation of a list of DOM-backed instances.
 *
 * The same shape the node renderer uses, factored out for the shell's own
 * lists — windows and room tabs. Keeping instances alive across updates is what
 * lets a window keep its running app and a room tab keep its rename box.
 */
export type Keyed = { el: Element };

export function reconcileKeyed<T, I extends Keyed>(
  host: HTMLElement,
  items: readonly T[],
  registry: Map<string, I>,
  hooks: {
    key: (item: T) => string;
    create: (item: T) => I;
    update: (instance: I, item: T) => void;
    destroy?: (instance: I) => void;
  },
): void {
  const next = new Map<string, I>();
  for (const item of items) {
    const key = hooks.key(item);
    const instance = registry.get(key) ?? hooks.create(item);
    hooks.update(instance, item);
    next.set(key, instance);
  }

  for (const [key, instance] of registry) {
    if (next.has(key)) continue;
    hooks.destroy?.(instance);
    instance.el.remove();
  }
  registry.clear();
  for (const [key, instance] of next) registry.set(key, instance);

  let anchor: ChildNode | null = host.firstChild;
  for (const item of items) {
    const instance = next.get(hooks.key(item));
    if (instance === undefined) continue;
    if (instance.el === anchor) anchor = anchor.nextSibling;
    else host.insertBefore(instance.el, anchor);
  }
}
